import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody, StatusPill, statusTone } from "@/components/PageChrome";
import { Input, Button } from "@/components/controls";
import { PackageOpen, Search, RefreshCw, ArrowRight, Truck, Boxes, Paperclip,
         FileText, ChevronDown, ChevronRight, X, Download, Plus, Trash2, Layers } from "lucide-react";
import { T } from "@/lib/testIds";
import { usePerms } from "@/lib/auth";
import ImageThumb from "@/components/ImageThumb";

const DEFAULT_DIVISOR = 5000;

// A blank package row for the multi-package section. Every field is a
// string in state so the input can be cleared without a controlled/
// uncontrolled React warning.
const BLANK_PACKAGE = () => ({
  actual_weight_kg: "",
  length_cm: "",
  width_cm: "",
  height_cm: "",
  volumetric_divisor: DEFAULT_DIVISOR,
  international_weight_kg: "",
  international_freight_charges: "",
});

function volumetricOfPkg(p) {
  const l = Number(p.length_cm) || 0;
  const w = Number(p.width_cm) || 0;
  const h = Number(p.height_cm) || 0;
  const div = Number(p.volumetric_divisor) || DEFAULT_DIVISOR;
  if (l <= 0 || w <= 0 || h <= 0 || div <= 0) return null;
  return Math.round(((l * w * h) / div) * 1000) / 1000;
}

// Text inputs need "" rather than null, otherwise React logs a controlled/
// uncontrolled warning and the field cannot be cleared.
function seedForm(d) {
  // Mar 3 2026 — multi-package. If server has packages[], seed from them;
  // else migrate legacy single-package fields into a one-item packages list
  // so the UI never renders zero packages for a shipment that has weight.
  const src = d?.packages && d.packages.length
    ? d.packages
    : (d && (d.actual_weight_kg || d.length_cm || d.width_cm || d.height_cm)
        ? [{
            actual_weight_kg: d.actual_weight_kg ?? "",
            length_cm: d.length_cm ?? "",
            width_cm: d.width_cm ?? "",
            height_cm: d.height_cm ?? "",
            volumetric_divisor: d.volumetric_divisor ?? DEFAULT_DIVISOR,
            international_weight_kg: "",
            international_freight_charges: d.international_freight_charges ?? "",
          }]
        : []);
  return {
    packages: src.map((p) => ({
      actual_weight_kg: p.actual_weight_kg ?? "",
      length_cm: p.length_cm ?? "",
      width_cm: p.width_cm ?? "",
      height_cm: p.height_cm ?? "",
      volumetric_divisor: p.volumetric_divisor ?? DEFAULT_DIVISOR,
      international_weight_kg: p.international_weight_kg ?? "",
      international_freight_charges: p.international_freight_charges ?? "",
    })),
    conversion_rate: d?.conversion_rate ?? "",
    comments: d?.comments ?? "",
  };
}

// /api/files/{id} is served by our own backend, so it needs the backend origin
// prefixed before it can be used as an href.
function fileHref(url) {
  if (!url) return null;
  return url.startsWith("/api/files/") ? `${API.replace(/\/api$/, "")}${url}` : url;
}

function DocSlot({ label, field, doc, uploading, editable, onUpload, onClear }) {
  const inputId = `inbound-doc-${field}`;
  return (
    <div className="flex items-center gap-2 min-w-0" data-testid={`inbound-doc-${field}`}>
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold w-24 shrink-0">
        {label}
      </div>
      {doc ? (
        <>
          <a
            href={fileHref(doc.url)}
            target="_blank"
            rel="noreferrer"
            data-testid={`inbound-doc-link-${field}`}
            className="inline-flex items-center gap-1 text-[12px] text-blue-700 underline truncate"
          >
            <FileText size={12} />
            <span className="truncate">{doc.name || "View"}</span>
          </a>
          {editable && (
            <button
              onClick={() => onClear(field)}
              title="Remove"
              data-testid={`inbound-doc-clear-${field}`}
              className="text-slate-400 hover:text-red-600"
            >
              <X size={12} />
            </button>
          )}
        </>
      ) : (
        <span className="text-[12px] text-slate-400">Not uploaded</span>
      )}
      {editable && (
        <>
          <input
            id={inputId}
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) onUpload(field, f);
            }}
          />
          <label
            htmlFor={inputId}
            data-testid={`inbound-doc-upload-${field}`}
            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border border-slate-200 rounded-sm bg-white text-slate-700 hover:bg-slate-50 cursor-pointer shrink-0"
          >
            <Paperclip size={11} />
            {uploading ? "Uploading\u2026" : doc ? "Replace" : "Upload"}
          </label>
        </>
      )}
    </div>
  );
}

export default function Inbound() {
  const [shipments, setShipments] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  // Shipment-level weight / dimensions / documents (item 8). Lives in its own
  // inbound_shipments collection, so none of this touches the order rows.
  const [details, setDetails] = useState(null);
  const [dForm, setDForm] = useState({});
  const [dOpen, setDOpen] = useState(false);
  const [dSaving, setDSaving] = useState(false);
  const [dUploading, setDUploading] = useState("");
  // Sep 3 2026 (ticket #243978) — one quantity input per collapsed china_id
  // group row, keyed by group key, so it doesn't collide with per-row _input_qty.
  const [groupQty, setGroupQty] = useState({});
  // Sep 7 2026 (ticket #243978) — which collapsed china_id groups are expanded
  // to show their individual orders, keyed by group key.
  const [expandedGroups, setExpandedGroups] = useState({});

  const saveDetails = async () => {
    if (!selectedId) return;
    setDSaving(true);
    try {
      const cleanNum = (v) => v === "" || v === null || v === undefined ? null : Number(v);
      const { data } = await api.put(`/inbound/${encodeURIComponent(selectedId)}/details`, {
        // Mar 3 2026 — packages[] replaces the old single-package fields.
        packages: (dForm.packages || []).map((p) => ({
          actual_weight_kg: cleanNum(p.actual_weight_kg),
          length_cm: cleanNum(p.length_cm),
          width_cm: cleanNum(p.width_cm),
          height_cm: cleanNum(p.height_cm),
          volumetric_divisor: cleanNum(p.volumetric_divisor) ?? DEFAULT_DIVISOR,
          international_weight_kg: cleanNum(p.international_weight_kg),
          international_freight_charges: cleanNum(p.international_freight_charges),
        })),
        conversion_rate: cleanNum(dForm.conversion_rate),
        comments: dForm.comments ?? null,
      });
      setDetails(data);
      setDForm(seedForm(data));
      toast.success("Shipment details saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDSaving(false);
    }
  };

  // Mar 3 2026 — download the current shipment as an XLSX workbook.
  const downloadXlsx = () => {
    if (!selectedId) return;
    const token = localStorage.getItem("access_token");
    const url = `${API.replace(/\/api$/, "")}/api/inbound/${encodeURIComponent(selectedId)}/export.xlsx`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("Download failed"); return r.blob(); })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = `inbound_${selectedId}.xlsx`;
        a.click();
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => toast.error("Download failed"));
  };

  const addPackage = () => {
    setDForm((f) => ({ ...f, packages: [...(f.packages || []), BLANK_PACKAGE()] }));
  };
  const removePackage = (idx) => {
    setDForm((f) => ({ ...f, packages: (f.packages || []).filter((_, i) => i !== idx) }));
  };
  const changePackage = (idx, key, val) => {
    setDForm((f) => ({
      ...f,
      packages: (f.packages || []).map((p, i) => i === idx ? { ...p, [key]: val } : p),
    }));
  };

  // Upload straight through to the shared /files/upload store, then persist the
  // reference immediately so a half-done upload can never be left unsaved.
  const uploadDoc = async (field, file) => {
    if (!selectedId || !file) return;
    setDUploading(field);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: up } = await api.post("/files/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data } = await api.put(`/inbound/${encodeURIComponent(selectedId)}/details`, {
        [field]: { id: up.id, url: up.url, name: up.original_filename,
                   content_type: up.content_type, size: up.size },
      });
      setDetails(data);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDUploading("");
    }
  };

  const clearDoc = async (field) => {
    if (!selectedId) return;
    if (!window.confirm("Remove this document from the shipment?")) return;
    try {
      const { data } = await api.put(`/inbound/${encodeURIComponent(selectedId)}/details`,
                                     { [field]: null });
      setDetails(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const { canEdit } = usePerms();
  const editable = canEdit("orders"); // Inbound shares perms with orders/procurement

  const loadShipments = async () => {
    try {
      // Mar 2 2026 — Inbound sidebar now shows two shipment sources side-
      // by-side: legacy China shipments (SHIP-…) from /inbound/shipments
      // AND Inventory-Transfer shipments (MOV{N}) from /transfers/shipments.
      // Both endpoints return the same row shape (see routes_transfers.py
      // docstring) so rendering below stays identical; only routing on
      // click differs.
      const [{ data: china }, { data: movs }] = await Promise.all([
        api.get("/inbound/shipments"),
        api.get("/transfers/shipments").catch(() => ({ data: [] })),
      ]);
      const merged = [...(china || []), ...(movs || [])];
      // Order by shipment number, newest first, within each series (MOV,
      // DSS-, ...): MOV109, MOV108, ... not by last activity, which put
      // MOV109 mid-list and ties scrambled the order (ticket 291868).
      // Series with the most recent activity is shown first.
      const splitId = (id) => {
        const m = String(id || "").match(/^(.*?)(\d+)\s*$/);
        return m ? [m[1].toUpperCase(), Number(m[2])] : [String(id || "").toUpperCase(), -1];
      };
      const seriesLatest = {};
      for (const s of merged) {
        const [p] = splitId(s.china_shipment_id);
        if ((s.last_updated || "") > (seriesLatest[p] || "")) seriesLatest[p] = s.last_updated;
      }
      merged.sort((a, b) => {
        const [pa, na] = splitId(a.china_shipment_id);
        const [pb, nb] = splitId(b.china_shipment_id);
        if (pa !== pb) {
          return (seriesLatest[pb] || "").localeCompare(seriesLatest[pa] || "") || pa.localeCompare(pb);
        }
        return nb - na;
      });
      setShipments(merged);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Routing helper. A shipment is an Inventory Transfer only if the merged
  // list actually tagged it `kind: "transfer"` -- NOT just because its id
  // happens to start with "MOV". A customer's own china_shipment_id can
  // coincidentally start with "MOV" too, and prefix-matching on the id
  // string alone misrouted those to /transfers, which 404s (ticket 258251).
  const isMov = (sid) => shipments.some((s) => s.china_shipment_id === sid && s.kind === "transfer");

  const loadShipment = async (sid) => {
    if (!sid) return;
    setLoading(true);
    setSelectedId(sid);
    setRows([]);
    try {
      // Route based on the shipment kind: MOV → /transfers, else → /inbound.
      const url = isMov(sid) ? `/transfers/${encodeURIComponent(sid)}` : `/inbound/${encodeURIComponent(sid)}`;
      const { data } = await api.get(url);
      setRows((data.rows || []).map((r) => ({
        ...r,
        // Mar 3 2026 — preserve saved delivered_qty even when it is 0; only
        // pre-fill with the ordered qty for rows that have NEVER been touched
        // (delivered_qty null/undefined). Previously `r.delivered_qty || r.qty`
        // treated 0 as unsaved and reset the input back to the ordered qty
        // on every refresh — the "Deliver Now bug".
        _input_qty: (r.delivered_qty !== null && r.delivered_qty !== undefined && r.delivered_qty !== "")
          ? Number(r.delivered_qty)
          : Number(r.china_qty_to_order || 0),
        _draft_china_id: r.china_id ?? "",
        _draft_comments: r.china_comments ?? r.comments ?? "",
        _dirty: false,
      })));
      // Transfers don't have inbound_shipments docs, so surface a synthetic
      // "empty details" so the details panel doesn't crash when opened.
      setDetails(data.details || null);
      setDForm(seedForm(data.details));
    } catch (e) {
      toast.error(formatApiError(e));
      setSelectedId("");
    }
    setLoading(false);
  };

  useEffect(() => { loadShipments(); }, []);

  const filteredShipments = useMemo(() => {
    if (!q) return shipments;
    const ql = q.toLowerCase();
    return shipments.filter((s) => (s.china_shipment_id || "").toLowerCase().includes(ql));
  }, [shipments, q]);

  const changeQty = (rid, v) => {
    setRows((rs) => rs.map((r) => (r.id === rid ? { ...r, _input_qty: v === "" ? "" : Number(v), _dirty: true } : r)));
  };
  const changeChinaId = (rid, v) => {
    setRows((rs) => rs.map((r) => (r.id === rid ? { ...r, _draft_china_id: v, _dirty: true } : r)));
  };
  const changeComments = (rid, v) => {
    setRows((rs) => rs.map((r) => (r.id === rid ? { ...r, _draft_comments: v, _dirty: true } : r)));
  };

  const saveRow = async (rid) => {
    const r = rows.find((x) => x.id === rid);
    if (!r) return;
    const patch = {};
    if ((r._draft_comments || "") !== (r.china_comments || r.comments || "")) {
      // Orders use `comments`, procurement uses `china_comments` — write both
      // so whichever collection the row lives in picks it up.
      patch.china_comments = r._draft_comments || "";
      patch.comments = r._draft_comments || "";
    }
    // Mar 3 2026 — delivered_qty inline save. Compares against BOTH ""/null
    // (unsaved) and the persisted server value; preserves an explicit 0
    // (previously "0 === falsy" collapsed 0 back to ordered qty on reload).
    const inputNum = r._input_qty === "" || r._input_qty === null || r._input_qty === undefined
      ? null : Number(r._input_qty);
    const savedNum = r.delivered_qty === "" || r.delivered_qty === null || r.delivered_qty === undefined
      ? null : Number(r.delivered_qty);
    if (inputNum !== null && inputNum !== savedNum) {
      patch.delivered_qty = inputNum;
    }
    if (!Object.keys(patch).length) {
      toast.message("Nothing to save on this row.");
      return;
    }
    try {
      const endpoint = r.source === "order" ? `/orders/${rid}` : `/procurement/${rid}`;
      const { data } = await api.patch(endpoint, patch);
      // Reflect saved values back into local state.
      setRows((rs) => rs.map((x) => x.id === rid ? {
        ...x,
        china_comments: data.china_comments ?? patch.china_comments ?? x.china_comments,
        comments: data.comments ?? patch.comments ?? x.comments,
        delivered_qty: data.delivered_qty ?? patch.delivered_qty ?? x.delivered_qty,
        _draft_comments: (data.china_comments ?? patch.china_comments ?? x.china_comments ?? x.comments ?? ""),
        _dirty: false,
      } : x));
      toast.success("Row saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveReceipt = async () => {
    if (!selectedId) return;
    // Mar 2 2026 — MOV shipments (Inventory Transfers) receive as a single
    // atomic action; there are no per-row delivered_qty inputs, so we hit
    // the transfers endpoint directly and rely on the server to add the
    // full quantity to destination inventory and close the shipment.
    if (isMov(selectedId)) {
      const row = rows[0];
      if (!row) return;
      if (row.status === "Received") {
        toast.message("This transfer has already been received.");
        return;
      }
      setBusy(true);
      try {
        const { data } = await api.post(`/transfers/${encodeURIComponent(selectedId)}/receive`);
        toast.success(`Received ${selectedId} — ${data.quantity} units added to ${data.destination_system_sku} @ ${data.destination_warehouse}`);
        await loadShipment(selectedId);
        await loadShipments();
      } catch (e) {
        toast.error(formatApiError(e));
      }
      setBusy(false);
      return;
    }
    const receipts = rows
      .filter((r) => Number(r._input_qty) > 0 && r.china_order_status !== "Delivered")
      .map((r) => ({ id: r.id, source: r.source, delivered_qty: Number(r._input_qty) }));
    if (!receipts.length) {
      toast.error("Enter delivered qty on at least one row");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/inbound/${encodeURIComponent(selectedId)}/receive`, { receipts });
      toast.success(`Received ${data.applied} row(s) across ${data.sku_updates} SKU(s)`);
      (data.errors || []).filter((x) => x.not_credited).forEach((x) =>
        toast.error(`${x.sku}: ${x.reason}`, { duration: 15000 }));
      await loadShipment(selectedId);
      await loadShipments();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  // Sep 3 2026 (ticket #243978) — customer wants the collapsed china_id
  // group row to be receivable like a single-order row: type one quantity,
  // it fills the earliest order first, then the next, until used up.
  // china_qty_to_order on a grouped row is the shared batch total (see the
  // "Ordered" summary column above), not any one order's own requirement,
  // so each order's true need is its own qty_purchased/qty. Only rows that
  // have never been received (delivered_qty <= 0) are eligible — receive
  // adds the submitted qty to inventory every call, so re-touching an
  // already-received row would double-count its stock.
  const distributeGroupQty = (g) => {
    const total = Number(groupQty[g.key]);
    if (!total || total <= 0) {
      toast.error("Enter a quantity to receive for this China ID");
      return null;
    }
    const eligible = g.rows
      .filter((r) => r.china_order_status !== "Delivered" && Number(r.delivered_qty || 0) <= 0)
      .slice()
      .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    let leftover = total;
    const updates = [];
    for (const r of eligible) {
      if (leftover <= 0) break;
      const need = Number(r.qty_purchased || r.qty || 1);
      const alloc = Math.min(need, leftover);
      if (alloc > 0) {
        updates.push({ id: r.id, source: r.source, delivered_qty: alloc });
        leftover -= alloc;
      }
    }
    if (!updates.length) {
      toast.error("No open orders left to receive under this China ID");
      return null;
    }
    if (leftover > 0) {
      toast.message(`${leftover} unit(s) left over after filling every open order under this China ID`);
    }
    return updates;
  };

  const saveGroupReceipt = async (g) => {
    const receipts = distributeGroupQty(g);
    if (!receipts) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/inbound/${encodeURIComponent(selectedId)}/receive`, { receipts });
      toast.success(`Received ${data.applied} row(s) across ${data.sku_updates} SKU(s)`);
      (data.errors || []).filter((x) => x.not_credited).forEach((x) =>
        toast.error(`${x.sku}: ${x.reason}`, { duration: 15000 }));
      setGroupQty((s) => ({ ...s, [g.key]: "" }));
      await loadShipment(selectedId);
      await loadShipments();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const totalOrdered = rows.reduce((sum, r) => sum + Number(r.china_qty_to_order || 0), 0);
  const totalInput = rows.reduce((sum, r) => sum + Number(r._input_qty || 0), 0);
  const totalDelivered = rows.reduce((sum, r) => sum + Number(r.delivered_qty || 0), 0);
  // Mar 2 2026 — detect a MOV (Inventory Transfer) shipment. Used to swap
  // in a transfer-specific table below and hide China-only affordances
  // (weight/dimensions panel, per-row Deliver Now inputs, etc.).
  const selectedIsMov = isMov(selectedId);
  const movRow = selectedIsMov ? rows[0] : null;
  const movReceived = selectedIsMov && movRow && movRow.status === "Received";
  const movPendingQty = selectedIsMov && movRow && !movReceived ? Number(movRow.quantity || 0) : 0;

  // Sep 2 2026 — display-only grouping (support ticket #241179). Each row
  // here is a real, individually-receivable order (receive_shipment posts
  // delivered_qty per row id), so nothing is merged or hidden — rows sharing
  // a China ID (fallback: System SKU) are just collapsed under one summary
  // line for readability. Expanding a group reveals the exact same
  // per-row Deliver Now inputs / Save buttons as before.
  const inboundGroupKey = (r) => {
    // Sep 7 2026 (ticket #245038) — china_id is entered by hand per purchase
    // and has been observed reused across unrelated SKUs in production.
    // Scoping to china_id + system_sku stops the group-receive workflow
    // below from distributing one typed quantity across two different
    // products that happen to share a China No.
    if (r.china_id !== null && r.china_id !== undefined && r.china_id !== "") return `cid:${r.china_id}:${r.system_sku || ""}`;
    if (r.system_sku) return `sku:${r.system_sku}`;
    return `row:${r.source}-${r.id}`;
  };
  const inboundGroups = useMemo(() => {
    const map = new Map();
    const order = [];
    const visibleRows = rows.filter(
      (r) => r.china_qty_raw !== null && r.china_qty_raw !== undefined && r.china_qty_raw !== ""
    );
    for (const r of visibleRows) {
      const key = inboundGroupKey(r);
      if (!map.has(key)) { map.set(key, { key, rows: [] }); order.push(key); }
      map.get(key).rows.push(r);
    }
    return order.map((k) => map.get(k));
  }, [rows]);
  const toggleGroup = (key) => setExpandedGroups((s) => ({ ...s, [key]: !s[key] }));
  // A group's Status column only shows a pill when every order under the
  // China ID currently shares the same china_order_status; otherwise it's
  // left blank rather than inventing a fake status like "Mixed" (ticket
  // #243978 — customer flagged "Mixed" as not a real China order status).
  const groupCommonStatus = (g) => {
    const first = g.rows[0]?.china_order_status ?? null;
    return g.rows.every((r) => (r.china_order_status ?? null) === first) ? first : null;
  };

  return (
    <>
      <PageHeader
        title="Inbound"
        subtitle="Receive China shipments — shift qty from in-transit into stock"
        testId={T.moduleTitle("inbound")}
        module="inbound"
      >
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={T.searchInput("inbound")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search shipment ID…"
            className="h-8 pl-7 pr-2 w-64 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <Button onClick={loadShipments} testId="inbound-refresh-btn">
          <RefreshCw size={13} /> Refresh
        </Button>
      </PageHeader>

      <PageBody>
        <div className="grid grid-cols-[280px_1fr] gap-4 min-h-[calc(100vh-200px)]">
          {/* Shipments sidebar */}
          <div className="border border-slate-200 bg-white rounded-sm overflow-hidden flex flex-col">
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                <Truck size={13} /> Shipments <span className="font-mono opacity-70">({filteredShipments.length})</span>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {filteredShipments.length === 0 && (
                <div className="p-6 text-center text-slate-500 text-[12px]">No shipments yet.</div>
              )}
              {filteredShipments.map((s) => {
                const pct = s.total_rows ? Math.round((s.delivered_rows / s.total_rows) * 100) : 0;
                const isActive = s.china_shipment_id === selectedId;
                const isTransfer = s.kind === "transfer";
                return (
                  <button
                    key={s.china_shipment_id}
                    data-testid={`inbound-shipment-${s.china_shipment_id}`}
                    onClick={() => loadShipment(s.china_shipment_id)}
                    className={`w-full text-left px-3 py-2 border-l-2 border-b border-slate-100 hover:bg-slate-50 ${
                      isActive ? "border-l-indigo-600 bg-indigo-50/40" : "border-l-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      {isTransfer && (
                        <span className="inline-flex items-center h-4 px-1 text-[8.5px] font-bold uppercase tracking-wider rounded-sm border bg-indigo-50 text-indigo-800 border-indigo-200 shrink-0">
                          MOV
                        </span>
                      )}
                      <div className="font-mono text-[12px] font-semibold text-slate-900 truncate">
                        {s.china_shipment_id}
                      </div>
                    </div>
                    {isTransfer ? (
                      <div className="text-[10.5px] text-slate-600 mt-0.5 font-mono">
                        {s.source_warehouse} → {s.destination_warehouse} · {s.quantity} units
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-1 text-[10.5px] text-slate-600">
                        <span className="font-mono">{s.delivered_rows}/{s.total_rows}</span>
                        <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono text-[10px]">{pct}%</span>
                      </div>
                    )}
                    {s.pending_rows > 0 && !isTransfer && (
                      <div className="text-[10px] text-amber-700 mt-0.5">{s.pending_rows} pending</div>
                    )}
                    {isTransfer && s.status && (
                      <div className={`text-[10px] mt-0.5 ${s.status === "Received" ? "text-emerald-700" : "text-amber-700"}`}>
                        {s.status}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Shipment detail */}
          <div className="border border-slate-200 bg-white rounded-sm overflow-hidden flex flex-col">
            {!selectedId && (
              <div className="flex-1 flex items-center justify-center text-slate-500 text-[13px]">
                <div className="text-center">
                  <PackageOpen size={40} className="mx-auto text-slate-300 mb-2" />
                  <div>Select a shipment on the left to receive it.</div>
                </div>
              </div>
            )}
            {selectedId && (
              <>
                <div className="h-12 border-b border-slate-200 flex items-center justify-between px-4">
                  <div>
                    <div className="font-heading text-[13.5px] font-semibold text-slate-900">
                      {selectedIsMov && (
                        <span className="inline-flex items-center h-5 px-1.5 mr-2 text-[9.5px] font-bold uppercase tracking-wider rounded-sm border bg-indigo-50 text-indigo-800 border-indigo-200">
                          Transfer
                        </span>
                      )}
                      Shipment <span className="font-mono text-indigo-800">{selectedId}</span>
                      {selectedIsMov && movRow && (
                        <span className="ml-2 text-[11px] font-normal text-slate-600 font-mono">
                          {movRow.source_warehouse} → {movRow.destination_warehouse}
                        </span>
                      )}
                    </div>
                    <div className="text-[10.5px] text-slate-500 font-mono">
                      {selectedIsMov
                        ? (movReceived
                            ? `Received • ${movRow?.quantity ?? 0} units moved to ${movRow?.destination_system_sku} @ ${movRow?.destination_warehouse}`
                            : `In Transit • ${movRow?.quantity ?? 0} units awaiting receive`)
                        : `${rows.length} rows • ${totalDelivered}/${totalOrdered} qty already received`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-[11px] text-slate-500">
                      Pending input: <span className="font-mono font-semibold text-slate-900" data-testid="inbound-pending-input">
                        {selectedIsMov ? movPendingQty : totalInput}
                      </span>
                    </div>
                    {/* Mar 3 2026 — download this shipment as XLSX (with images). */}
                    {!selectedIsMov && (
                      <Button onClick={downloadXlsx} testId="inbound-download-xlsx">
                        <Download size={13} /> Download XLSX
                      </Button>
                    )}
                    {editable && (
                      <Button
                        variant="primary"
                        onClick={saveReceipt}
                        // Sep 22 2026 (spec) — hide the pointer / grey the
                        // button out when every shipment line is already
                        // Delivered. Per-row inputs are already locked
                        // (`✓ Done`); this stops a stray top-level click
                        // from firing a no-op POST that the backend
                        // would reject with "Already received" anyway.
                        disabled={busy || loading
                                  || (selectedIsMov && movReceived)
                                  || (!selectedIsMov && rows.length > 0
                                      && rows.every((r) => r.china_order_status === "Delivered"))}
                        testId="inbound-receive-btn"
                      >
                        <ArrowRight size={13} />
                        {busy ? "Receiving…"
                          : (selectedIsMov && movReceived)
                            ? "Received"
                            : (!selectedIsMov && rows.length > 0
                               && rows.every((r) => r.china_order_status === "Delivered"))
                              ? "All Received"
                              : "Receive & Move to Stock"}
                      </Button>
                    )}
                  </div>
                </div>
                {/* Shipment weight/dimensions/documents apply only to legacy
                    China shipments — hide entirely for MOV transfers. */}
                {!selectedIsMov && (
                <div className="border-b border-slate-200 bg-slate-50/60" data-testid="inbound-details-panel">
                  <button
                    onClick={() => setDOpen((v) => !v)}
                    data-testid="inbound-details-toggle"
                    className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-slate-100/60"
                  >
                    {dOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                      Shipment weight, dimensions &amp; documents
                    </span>
                    <span className="ml-auto flex items-center gap-3 text-[10.5px] font-mono text-slate-600">
                      {details?.actual_weight_kg != null && <span>Actual {details.actual_weight_kg} kg</span>}
                      {details?.volumetric_weight_kg != null && <span>Volumetric {details.volumetric_weight_kg} kg</span>}
                      {details?.packing_list && <span className="text-emerald-700">Packing list</span>}
                      {details?.invoice && <span className="text-emerald-700">Invoice</span>}
                    </span>
                  </button>
                  {dOpen && (
                    <div className="px-4 pb-3 space-y-3">
                      {/* Mar 3 2026 — multi-package section. Each package has
                          its own weight/L/W/H/divisor/intl-weight/intl-freight,
                          and volumetric_weight_kg auto-computes from L·W·H/div. */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">
                            Packages ({(dForm.packages || []).length})
                          </span>
                          {editable && (
                            <button
                              type="button"
                              onClick={addPackage}
                              data-testid="inbound-add-package"
                              className="inline-flex items-center gap-1 h-6 px-2 text-[10.5px] font-semibold rounded-sm border border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
                            >
                              <Plus size={11} /> Add Package
                            </button>
                          )}
                        </div>
                        {(dForm.packages || []).length === 0 && (
                          <div className="text-[11.5px] text-slate-500 py-2">
                            No packages yet. {editable && <>Click <strong>+ Add Package</strong> to record shipment weights and dimensions.</>}
                          </div>
                        )}
                        {(dForm.packages || []).map((p, idx) => (
                          <div
                            key={idx}
                            data-testid={`inbound-package-${idx}`}
                            className="rounded-sm border border-slate-200 bg-white px-3 py-2"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[10.5px] uppercase tracking-wider font-bold text-slate-700">
                                Package #{idx + 1}
                              </span>
                              {editable && (
                                <button
                                  type="button"
                                  onClick={() => removePackage(idx)}
                                  data-testid={`inbound-remove-package-${idx}`}
                                  title="Remove this package"
                                  className="text-slate-400 hover:text-red-600"
                                >
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                            <div className="flex flex-wrap items-end gap-3">
                              {[
                                ["actual_weight_kg", "Actual Weight (kg)"],
                                ["length_cm", "Length (cm)"],
                                ["width_cm", "Width (cm)"],
                                ["height_cm", "Height (cm)"],
                                ["volumetric_divisor", "Divisor"],
                                ["international_weight_kg", "Intl. Weight (kg)"],
                                ["international_freight_charges", "Intl. Freight ₹"],
                              ].map(([k, lbl]) => (
                                <div key={k} className="flex flex-col gap-1">
                                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{lbl}</label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    disabled={!editable}
                                    value={p[k] ?? ""}
                                    onChange={(e) => changePackage(idx, k, e.target.value)}
                                    data-testid={`inbound-package-${idx}-${k}`}
                                    className="w-28 px-2 py-1 text-[12px] font-mono border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50"
                                  />
                                </div>
                              ))}
                              <div className="flex flex-col gap-1">
                                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Volumetric (kg)</label>
                                <div
                                  data-testid={`inbound-package-${idx}-volumetric`}
                                  className="w-28 px-2 py-1 text-[12px] font-mono border border-dashed border-slate-300 rounded-sm bg-white text-slate-700"
                                >
                                  {volumetricOfPkg(p) ?? "-"}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Conversion rate</label>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            disabled={!editable}
                            value={dForm.conversion_rate ?? ""}
                            onChange={(e) => setDForm((f) => ({ ...f, conversion_rate: e.target.value }))}
                            data-testid="inbound-detail-conversion_rate"
                            className="w-28 px-2 py-1 text-[12px] font-mono border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50"
                          />
                        </div>
                        {editable && (
                          <Button
                            variant="primary"
                            onClick={saveDetails}
                            disabled={dSaving}
                            testId="inbound-details-save"
                          >
                            {dSaving ? "Saving..." : "Save details"}
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 max-w-3xl">
                        <DocSlot
                          label="Packing list"
                          field="packing_list"
                          doc={details?.packing_list}
                          uploading={dUploading === "packing_list"}
                          editable={editable}
                          onUpload={uploadDoc}
                          onClear={clearDoc}
                        />
                        <DocSlot
                          label="Invoice"
                          field="invoice"
                          doc={details?.invoice}
                          uploading={dUploading === "invoice"}
                          editable={editable}
                          onUpload={uploadDoc}
                          onClear={clearDoc}
                        />
                      </div>
                      <div className="max-w-3xl">
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Comments</label>
                        <textarea
                          rows={3}
                          disabled={!editable}
                          value={dForm.comments ?? ""}
                          onChange={(e) => setDForm((f) => ({ ...f, comments: e.target.value }))}
                          data-testid="inbound-detail-comments"
                          placeholder="Notes about this shipment (customs, damage, follow-ups…)"
                          className="w-full px-2 py-1.5 text-[12px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50"
                        />
                      </div>
                      <div className="text-[10.5px] text-slate-500">
                        Volumetric weight per package = length x width x height / divisor
                        (5000 is the usual air-freight divisor for cm and kg). All packages
                        are saved against the shipment.
                      </div>
                    </div>
                  )}
                </div>
                )}
                {loading ? (
                  <div className="flex-1 flex items-center justify-center text-slate-500 text-[13px]">
                    Loading…
                  </div>
                ) : selectedIsMov ? (
                  /* Mar 2 2026 — MOV shipments render a compact 4-column
                     transfer table (system_sku, source, destination, qty)
                     since no per-row Deliver Now input is meaningful. */
                  <div className="flex-1 overflow-auto p-4">
                    <table className="erp-table" data-testid="inbound-transfer-table">
                      <thead>
                        <tr>
                          <th>System SKU</th>
                          <th>Source Warehouse</th>
                          <th>Destination SKU</th>
                          <th>Destination Warehouse</th>
                          <th className="text-right">Quantity</th>
                          <th className="text-right">Source Stock (now)</th>
                          <th className="text-right">Destination Stock (now)</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.id} data-testid={`inbound-mov-row-${r.id}`}
                              className={r.status === "Received" ? "bg-emerald-50/40" : ""}>
                            <td className="font-mono">{r.source_system_sku || r.system_sku || "—"}</td>
                            <td className="font-mono text-[11.5px]">{r.source_warehouse || "—"}</td>
                            <td className="font-mono">{r.destination_system_sku || "—"}</td>
                            <td className="font-mono text-[11.5px]">{r.destination_warehouse || "—"}</td>
                            <td className="text-right font-mono font-semibold">{r.quantity ?? 0}</td>
                            <td className="text-right font-mono text-slate-700">{r.current_source_stock ?? 0}</td>
                            <td className="text-right font-mono text-slate-700">{r.current_destination_stock ?? 0}</td>
                            <td>
                              <StatusPill value={r.status} tone={r.status === "Received" ? "green" : "amber"} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto">
                    {(() => {
                    const renderInboundRow = (r) => {
                      const done = r.china_order_status === "Delivered";
                          // Mar 3 2026 — visual "saved" indicator: any row
                          // that already has a delivered_qty on the server
                          // (>0) is a partial-receive that has been persisted,
                          // so tint the row emerald so ops can see at a glance
                          // which rows have been saved vs still-dirty.
                          const saved = !done && Number(r.delivered_qty || 0) > 0;
                          return (
                            <tr key={r.id} data-testid={`inbound-row-${r.id}`}
                                data-saved={saved ? "1" : "0"}
                                className={done ? "bg-emerald-50/40" : (saved ? "bg-emerald-50/70" : "")}>
                              <td>
                                <span className={`inline-flex px-1.5 h-5 text-[9.5px] font-bold uppercase tracking-wider rounded-sm border ${
                                  r.source === "order" ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"
                                }`}>
                                  {r.source === "order" ? "Order" : "Direct"}
                                </span>
                              </td>
                              <td>
                                {r.image_url
                                  ? <ImageThumb src={r.image_url} size={64} testId={`inbound-thumb-${r.id}`} />
                                  : "—"}
                              </td>
                              <td className="font-mono text-[11.5px]" data-testid={`inbound-system-id-${r.id}`}>
                                {r.system_id ?? "—"}
                              </td>
                              <td className="font-mono">{r.system_sku || "—"}</td>
                              <td className="max-w-[260px]">
                                <div className="truncate text-[12px] text-slate-900">{r.product_name || "—"}</div>
                                <div className="truncate text-[10.5px] text-slate-500">{r.variant || ""}</div>
                              </td>
                              {/* Mar 3 2026 — China ID is now READ-ONLY in the
                                  Inbound row (same style as System SKU). If a
                                  row needs a china_id assigned, do that from
                                  the Orders / Procurement modules. */}
                              <td className="font-mono text-[11.5px]" data-testid={`inbound-china-id-${r.id}`}>
                                {r.china_id ?? "—"}
                              </td>
                              <td>
                                <div className="inline-flex items-center gap-1">
                                  <StatusPill value={r.china_order_status} tone={statusTone(r.china_order_status)} />
                                  {saved && (
                                    <span
                                      title="Deliver Now value is saved"
                                      data-testid={`inbound-saved-${r.id}`}
                                      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-600 text-white text-[9px] font-bold"
                                    >✓</span>
                                  )}
                                </div>
                              </td>
                              <td className="text-right font-mono">{r.china_qty_to_order || 0}</td>
                              <td className="text-right font-mono text-slate-600">{r.delivered_qty || 0}</td>
                              <td className="text-right">
                                {done ? (
                                  <span className="text-emerald-700 font-mono text-[11.5px]">✓ Done</span>
                                ) : (
                                  <Input
                                    type="number"
                                    testId={`inbound-qty-${r.id}`}
                                    value={r._input_qty ?? ""}
                                    onChange={(e) => changeQty(r.id, e.target.value)}
                                    className={`w-20 text-right font-mono ${saved ? "border-emerald-300 bg-emerald-50/50" : ""}`}
                                  />
                                )}
                              </td>
                              {/* Mar 3 2026 — read-only tax fields pulled from
                                  the linked procurement row (join in backend). */}
                              <td className="text-[11.5px]" data-testid={`inbound-tax-name-${r.id}`}>{r.tax_name || "—"}</td>
                              <td className="text-right font-mono text-[11.5px]" data-testid={`inbound-tax-rate-${r.id}`}>
                                {r.tax_rate != null && r.tax_rate !== "" ? `${r.tax_rate}` : "—"}
                              </td>
                              <td className="font-mono text-[11.5px]" data-testid={`inbound-hsn-${r.id}`}>{r.hsn_code || "—"}</td>
                              {/* Mar 3 2026 — package_no + total_price from procurement */}
                              <td className="font-mono text-[11.5px]" data-testid={`inbound-package-no-${r.id}`}>{r.package_no || "—"}</td>
                              <td className="text-right font-mono text-[11.5px] font-semibold" data-testid={`inbound-total-price-${r.id}`}>{r.total_price != null && r.total_price !== "" ? `¥${r.total_price}` : "—"}</td>
                              <td className="text-right font-mono">
                                <span className="inline-flex items-center gap-1 text-slate-900">
                                  <Boxes size={11} className="text-slate-400" />
                                  {r.current_stock ?? 0}
                                </span>
                              </td>
                              <td className="text-right font-mono text-amber-700">{r.current_in_transit ?? 0}</td>
                              <td className="max-w-[180px]">
                                {editable ? (
                                  <Input
                                    testId={`inbound-comments-${r.id}`}
                                    value={r._draft_comments ?? ""}
                                    onChange={(e) => changeComments(r.id, e.target.value)}
                                    placeholder="—"
                                    className="w-40 text-[11.5px]"
                                  />
                                ) : (
                                  <span className="truncate text-[11px] text-slate-600">{r.china_comments || r.comments || "—"}</span>
                                )}
                              </td>
                              <td>
                                {editable && !done && (
                                  <button
                                    type="button"
                                    onClick={() => saveRow(r.id)}
                                    data-testid={`inbound-save-row-${r.id}`}
                                    disabled={!r._dirty}
                                    className={`h-6 px-2 text-[10.5px] font-semibold rounded-sm inline-flex items-center gap-1 border ${
                                      r._dirty
                                        ? "border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
                                        : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                                    }`}
                                    title={r._dirty ? "Save this row's edits" : "No unsaved changes"}
                                  >
                                    Save
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                    };
                    return (
                    <table className="erp-table">
                      <thead>
                        <tr>
                          <th>Source</th>
                          <th>Image</th>
                          <th>System ID</th>
                          <th>System SKU</th>
                          <th>Product / Variant</th>
                          <th>China ID</th>
                          <th>Status</th>
                          <th className="text-right">Qty to Order</th>
                          <th className="text-right">Delivered Qty</th>
                          <th className="text-right">Deliver Now</th>
                          <th>Tax Name</th>
                          <th className="text-right">Tax %</th>
                          <th>HSN</th>
                          {/* Mar 3 2026 — Package No + Total Price (joined
                              read-only from the linked procurement row). */}
                          <th>Package No</th>
                          <th className="text-right">Total ¥</th>
                          <th className="text-right">Current Stock</th>
                          <th className="text-right">In Transit</th>
                          <th>Comments</th>
                          <th></th>
                        </tr>
                      </thead>
                   <tbody>
  {inboundGroups.map((g) => {
    if (g.rows.length === 1) return renderInboundRow(g.rows[0]);
    const first = g.rows[0];
    const isOpen = !!expandedGroups[g.key];
    const orderedSum = g.rows.reduce((s, r) => s + Number(r.china_qty_to_order || 0), 0);
    const deliveredSum = g.rows.reduce((s, r) => s + Number(r.delivered_qty || 0), 0);
    const commonStatus = groupCommonStatus(g);
    const allDone = g.rows.every((r) => r.china_order_status === "Delivered");
    return (
      <React.Fragment key={g.key}>
        <tr data-testid={`inbound-group-${g.key}`} className="bg-slate-50/70">
          <td>
            <button
              type="button"
              onClick={() => toggleGroup(g.key)}
              data-testid={`inbound-group-toggle-${g.key}`}
              className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-slate-700"
            >
              {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              <Layers size={11} className="text-slate-400" />
              {g.rows.length}
            </button>
          </td>
          <td>
            {first.image_url
              ? <ImageThumb src={first.image_url} size={64} testId={`inbound-group-thumb-${first.id}`} />
              : "—"}
          </td>
          <td className="font-mono text-[11.5px] text-slate-400">—</td>
          <td className="font-mono">{first.system_sku || "—"}</td>
          <td className="max-w-[260px]">
            <div className="truncate text-[12px] text-slate-900">{first.product_name || "—"}</div>
            <div className="truncate text-[10.5px] text-slate-500">{first.variant || ""}</div>
          </td>
          <td className="font-mono text-[11.5px]" data-testid={`inbound-group-china-id-${g.key}`}>
            {first.china_id ?? "—"}
          </td>
          <td data-testid={`inbound-group-status-${g.key}`}>
            {commonStatus
              ? <StatusPill value={commonStatus} tone={statusTone(commonStatus)} />
              : <span className="text-slate-400 text-[11px]">—</span>}
          </td>
          <td className="text-right font-mono">{orderedSum}</td>
          <td className="text-right font-mono text-slate-600">{deliveredSum}</td>
          <td className="text-right">
            {allDone ? (
              <span className="text-emerald-700 font-mono text-[11.5px]">✓ Done</span>
            ) : (
              <Input
                type="number"
                testId={`inbound-group-qty-${g.key}`}
                value={groupQty[g.key] ?? ""}
                onChange={(e) => setGroupQty((s) => ({ ...s, [g.key]: e.target.value }))}
                className="w-20 text-right font-mono"
              />
            )}
          </td>
          <td className="text-[11.5px] text-slate-400">—</td>
          <td className="text-right font-mono text-[11.5px] text-slate-400">—</td>
          <td className="font-mono text-[11.5px] text-slate-400">—</td>
          <td className="text-right font-mono">
            <span className="inline-flex items-center gap-1 text-slate-900">
              <Boxes size={11} className="text-slate-400" />
              {first.current_stock ?? 0}
            </span>
          </td>
          <td className="text-right font-mono text-amber-700">{first.current_in_transit ?? 0}</td>
          <td className="text-slate-400 text-[11px]">—</td>
          <td>
            {editable && !allDone && (
              <button
                type="button"
                onClick={() => saveGroupReceipt(g)}
                data-testid={`inbound-group-save-${g.key}`}
                disabled={!groupQty[g.key]}
                className={`h-6 px-2 text-[10.5px] font-semibold rounded-sm inline-flex items-center gap-1 border ${
                  groupQty[g.key]
                    ? "border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
                    : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                }`}
                title="Receive this quantity, filling the earliest open orders under this China ID first"
              >
                Save
              </button>
            )}
          </td>
        </tr>
        {isOpen && g.rows.map((r) => renderInboundRow(r))}
      </React.Fragment>
    );
  })}
</tbody>
                    </table>
                    );
                    })()}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </PageBody>
    </>
  );
}
